import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { searchTopBases, searchChpps, searchPrivatePoints } from 'service/AdminPanelService'
import { getErpErrorMessage } from 'service/ErpService'

/**
 * Компонент для поиска и выбора базы (ТОП-База, ТЭЦ, Соц.объект/МСУ)
 * с динамической подгрузкой результатов по поиску
 */
const BaseSearchSelect = ({ 
  baseType = 'top_base', // 'top_base' | 'chpp' | 'private_point'
  value = '', // ID выбранной базы
  onChange = () => {}, // callback(id)
  placeholder = tr("Поиск базы..."),
  disabled = false,
  activeOnly = false,
  searchAriaLabel,
  selectAriaLabel,
  style = {}
}) => {
  useInterfaceLanguage();
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const debounceTimer = useRef(null)
  const requestId = useRef(0)
  const selectedBase = useRef(null)

  // Функция для выполнения поиска
  const performSearch = useCallback(async (query) => {
    const currentRequest = ++requestId.current
    setLoadError('')
    if (!query || query.trim().length < 1) {
      setSearchResults([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const search = baseType === 'chpp' ? searchChpps : baseType === 'private_point' ? searchPrivatePoints : searchTopBases
      const response = activeOnly ? await search(query.trim(), 20, { activeOnly: true }) : await search(query.trim(), 20)
      
      const data = response?.data?.data || response?.data || []
      if (currentRequest === requestId.current) setSearchResults(Array.isArray(data) ? data.filter(base => !activeOnly || base.is_active !== false) : [])
    } catch (error) {
      if (currentRequest === requestId.current) {
        setLoadError(getErpErrorMessage(error))
        setSearchResults([])
      }
    } finally {
      if (currentRequest === requestId.current) setIsLoading(false)
    }
  }, [baseType, activeOnly])

  // Обработка изменения поискового запроса с debounce
  const handleSearchChange = (e) => {
    const query = e.target.value
    setSearchQuery(query)

    // Очищаем предыдущий таймер
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    requestId.current += 1

    // Устанавливаем новый таймер
    debounceTimer.current = setTimeout(() => {
      performSearch(query)
    }, 300) // 300ms задержка
  }

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      requestId.current += 1
    }
  }, [])

  // Загружаем начальные результаты при монтировании или смене типа базы
  useEffect(() => {
    performSearch('')
  }, [baseType, performSearch])

  const selected = selectedBase.current?.type === baseType && String(selectedBase.current?.base?.id) === String(value)
    ? selectedBase.current.base : null
  const visibleResults = selected && !searchResults.some(base => String(base.id) === String(selected.id))
    ? [selected, ...searchResults] : searchResults

  return (
    <div style={{ display: 'inline-block', minWidth: 220, ...style }}>
      <input 
        placeholder={tr(placeholder)}
        aria-label={searchAriaLabel}
        disabled={disabled}
        value={searchQuery} 
        onChange={handleSearchChange}
        style={{ width: '100%', marginBottom: 4 }}
      />
      <select 
        value={value || ''}
        aria-label={selectAriaLabel}
        onChange={(e) => {
          const id = e.target.value ? Number(e.target.value) : null
          selectedBase.current = id ? { type: baseType, base: visibleResults.find(base => String(base.id) === String(id)) } : null
          onChange(id)
        }}
        style={{ width: '100%' }}
        disabled={disabled || isLoading}
      >
        <option value="">{tr("— Выберите —")}</option>
        {visibleResults.map(base => (
          <option key={base.id} value={base.id}>
            {tr(base.name)}
          </option>
        ))}
      </select>
      {tr(isLoading && (
        <div role="status" style={{ fontSize: '12px', color: "var(--theme-text-muted, #666)", marginTop: 2 }}>{tr("Загрузка...")}</div>
      ))}
      {loadError && <p role="alert">{tr(loadError)}</p>}
    </div>
  )
}

export default BaseSearchSelect
